import {
	Grid,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	CardMedia,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import type { EmMachine } from "@packsize/schemas/machines";

import EmMachineImage from "@/assets/images/machine/em.png";
import RollForwardImage from "@/assets/images/machine/em/manual-control/roll-forward.svg";
import RollBackwardImage from "@/assets/images/machine/em/manual-control/roll-backward.svg";
import CleanCutImage from "@/assets/images/machine/em/manual-control/clean-cut.svg";
import type { EmServiceCommand } from "@/api/machines/em-api-v2";

const actions: readonly {
	readonly action: EmServiceCommand;
	readonly image: string;
	readonly testId: string;
}[] = [
	{
		action: "feedforward",
		image: RollForwardImage,
		testId: "em-manual-control-card__button__roll-forward",
	},
	{
		action: "feedbackwards",
		image: RollBackwardImage,
		testId: "em-manual-control-card__button__roll-backward",
	},
	{
		action: "cleancut",
		image: CleanCutImage,
		testId: "em-manual-control-card__button__clean-cut",
	},
];

const getActionMessage = (action: EmServiceCommand, intl: IntlShape) => {
	switch (action) {
		case "feedforward":
			return intl.formatMessage({ id: "Roll Forward", defaultMessage: "Roll Forward" });
		case "feedbackwards":
			return intl.formatMessage({ id: "Roll Backward", defaultMessage: "Roll Backward" });
		case "cleancut":
			return intl.formatMessage({ id: "Clean Cut", defaultMessage: "Clean Cut" });
	}
};

const getActionAlt = (action: EmServiceCommand, intl: IntlShape) => {
	switch (action) {
		case "feedforward":
			return intl.formatMessage({ id: "Roll Forward", defaultMessage: "Roll Forward" });
		case "feedbackwards":
			return intl.formatMessage({ id: "Roll Backward", defaultMessage: "Roll Backward" });
		case "cleancut":
			return intl.formatMessage({ id: "Clean Cut", defaultMessage: "Clean Cut" });
	}
};

export interface EmManualControlCardTemplateProps {
	readonly machine: EmMachine;
	readonly selectedTrackNumber: number;
	readonly executingAction: EmServiceCommand | null;
	readonly onTrackChange: (trackNumber: number) => void;
	readonly onAction: (action: EmServiceCommand) => Promise<void>;
}

export const EmManualControlCardTemplate = ({
	machine,
	selectedTrackNumber,
	executingAction,
	onTrackChange,
	onAction,
}: EmManualControlCardTemplateProps) => {
	const intl = useIntl();

	return (
		<Card>
			<CardHeader
				title={<Typography variant="h2">{machine.alias}</Typography>}
				subheader={intl.formatMessage(
					{
						id: "Machine type and status",
						defaultMessage: "{machineType} · {status}",
					},
					{ machineType: machine.machineType, status: machine.status },
				)}
			/>
			<CardContent>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 280px) minmax(0, 1fr)" },
						gap: 2,
					}}
				>
					<CardMedia
						component="img"
						image={EmMachineImage}
						alt={intl.formatMessage({ id: "Machine", defaultMessage: "Machine" })}
						sx={{ width: "100%", maxHeight: 260, objectFit: "contain", p: 2 }}
					/>

					{machine.status !== "Paused" && (
						<Grid
							container
							direction="column"
							justifyContent="center"
							alignItems="center"
							gap={2}
							sx={{
								minHeight: 200,
							}}
						>
							<ErrorIcon color="error" sx={{ fontSize: 72 }} />
							<Typography variant="h3">
								<FormattedMessage
									id="Machine must be paused to manually control"
									defaultMessage="Machine must be paused to manually control"
								/>
							</Typography>
						</Grid>
					)}

					{machine.status === "Paused" && (
						<Stack spacing={2}>
							<FormControl component="fieldset">
								<Typography component="legend" variant="h3" sx={{ mb: 1 }}>
									<FormattedMessage id="Select Track" defaultMessage="Select Track" />
								</Typography>

								<RadioGroup
									row
									value={String(selectedTrackNumber)}
									onChange={(event) => onTrackChange(Number(event.target.value))}
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(3, minmax(140px, 1fr))",
										gap: 2,
									}}
								>
									{machine.tracks.map((track) => (
										<Card
											key={track.number}
											variant="outlined"
											sx={{
												borderColor: selectedTrackNumber === track.number ? "primary.main" : "divider",
											}}
										>
											<FormControlLabel
												value={String(track.number)}
												control={<Radio />}
												label={intl.formatMessage(
													{
														id: "Track {trackNumber}",
														defaultMessage: "Track {trackNumber}",
													},
													{ trackNumber: track.number },
												)}
												sx={{ m: 0, p: 1, width: "100%" }}
												data-testid={`em-manual-control-card__radio__track-${track.number}`}
											/>
										</Card>
									))}
								</RadioGroup>
							</FormControl>

							<Grid display="grid" gap={2} gridTemplateColumns="repeat(3, minmax(0, 1fr))">
								{actions.map(({ action, image, testId }) => (
									<Card key={action} variant="outlined">
										<CardMedia component="img" image={image} alt={getActionAlt(action, intl)} sx={{ p: 2 }} />

										<CardActions>
											<Button
												variant="contained"
												fullWidth
												size="large"
												loading={executingAction === action}
												disabled={executingAction !== null}
												onClick={() => void onAction(action)}
												data-testid={testId}
											>
												{getActionMessage(action, intl)}
											</Button>
										</CardActions>
									</Card>
								))}
							</Grid>
						</Stack>
					)}
				</Box>
			</CardContent>
		</Card>
	);
};
